/*eslint-disable*/
import React, { useMemo } from 'react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  TableSortLabel,
} from '@mui/material'

// helpers
import {
  getFilteredMembers,
  isMemberSelected,
  areAllMembersChecked,
  getGroupMemberIds,
  getSelectedMembersWithOutSelectedMemberId,
  shouldDisableSelectAll,
  shouldDisableMember,
  getSelectedMembersCount,
  sortMembers,
} from './groupMembers.helpers'

// constants
import { MAX_ALLOWED_MEMBERS } from './groupMembers.constants'

// hooks
import useSort from '../../../../hooks/useSort'

// styles
import styles from './groupMembers.module.scss'

import { useIsMobileView } from '../../../../utils/isMobileView'

import { getEmail } from '../../../../helpers/users'

const GroupMembers = ({
  group,
  allUsers,
  searchText,
  isLoading,
  selectedMembers,
  setSelectedMembers,
}) => {
  const [page, setPage] = React.useState(0)

  const [rowsPerPage, setRowsPerPage] = React.useState(10)

  const { stats } = group

  const { t } = useTranslation()
  const isMobileView = useIsMobileView()

  const { sortBy, sortOrder, handleSort } = useSort({
    initialSortBy: 'display',
    initialSortOrder: 'asc',
  })

  const members = useMemo(() => {
    setPage(0)
    return getFilteredMembers(group, searchText)
  }, [group, searchText])

  const allMembersChecked = areAllMembersChecked(members, selectedMembers)

  const handleSelectAllCheckBoxChange = () => {
    let updatedSelectedMembers = []
    const memberIds = getGroupMemberIds(group)
    if (allMembersChecked) {
      updatedSelectedMembers = selectedMembers.filter(
        (selectedMember) => !memberIds.includes(selectedMember)
      )
    } else {
      updatedSelectedMembers = [...selectedMembers]
      memberIds.forEach((memberId) => {
        if (!isMemberSelected(memberId, selectedMembers)) {
          updatedSelectedMembers.push(memberId)
        }
      })
    }
    setSelectedMembers(updatedSelectedMembers)
  }

  const handleCheckboxChange = (member) => {
    let updatedSelectedMembers = []
    const selectedMemberId = member?.value
    if (isMemberSelected(selectedMemberId, selectedMembers)) {
      updatedSelectedMembers = getSelectedMembersWithOutSelectedMemberId(
        selectedMembers,
        selectedMemberId
      )
    } else {
      updatedSelectedMembers = [...selectedMembers, selectedMemberId]
    }
    const selectedMembersCount = getSelectedMembersCount(updatedSelectedMembers)
    if (selectedMembersCount >= MAX_ALLOWED_MEMBERS) {
      toast.warning(t('members-table-cannot-add-remove-more-than-five'))
    }
    setSelectedMembers(updatedSelectedMembers)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const sortedMembers = useMemo(
    () => sortMembers({ members, sortBy, sortOrder }),
    [members, sortBy, sortOrder]
  )

  const renderTableHead = () => (
    <TableRow>
      <TableCell className={styles.tableHeader}>
        <TableSortLabel
          className={styles.userTableHeaderLabel}
          active={sortBy === 'display'}
          direction={sortOrder}
          onClick={() => handleSort('display')}
        >
          {t('edit-members-modal-members-name-table')}
        </TableSortLabel>
      </TableCell>
      <TableCell className={styles.tableHeader}>
        <TableSortLabel
          className={styles.userTableHeaderLabel}
          active={sortBy === 'email'}
          direction={sortOrder}
          onClick={() => handleSort('email')}
        >
          {t('members-table-email')}
        </TableSortLabel>
      </TableCell>
      <TableCell
        className={styles.tableHeader}
        sx={{ fontFamily: 'Johnson Text', marginLeft: '70px' }}
      >
        <FormControlLabel
          control={
            <Checkbox
              sx={{
                color: shouldDisableSelectAll(members, selectedMembers)
                  ? 'grey'
                  : '#5E5047',
                '&.Mui-checked': {
                  color: shouldDisableMember(members, selectedMembers)
                    ? 'grey'
                    : '#000000',
                },
              }}
              disabled={shouldDisableSelectAll(members, selectedMembers)}
              checked={allMembersChecked}
              onChange={handleSelectAllCheckBoxChange}
            />
          }
        />
        {t('edit-members-select-all-delete')}
      </TableCell>
    </TableRow>
  )

  const renderTableBody = () => {
    return sortedMembers
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((member) => (
        <TableRow className={styles.tableBodyRow} key={member.value}>
          <TableCell sx={{ width: '35%' }} className={styles.tableCell}>
            {member?.display}
          </TableCell>
          <TableCell className={styles.tableCell}>{member?.email}</TableCell>
          <TableCell sx={{ width: '25%' }}>
            <FormControlLabel
              control={
                <Checkbox
                  disabled={shouldDisableMember(member, selectedMembers)}
                  checked={isMemberSelected(member.value, selectedMembers)}
                  onChange={() => handleCheckboxChange(member)}
                  sx={{
                    color: shouldDisableMember(member, selectedMembers)
                      ? 'grey'
                      : '#5E5047',
                    '&.Mui-checked': {
                      color: shouldDisableMember(member, selectedMembers)
                        ? 'grey'
                        : '#000000',
                    },
                  }}
                />
              }
            />
            {t('edit-members-checkbox-select')}
          </TableCell>
        </TableRow>
      ))
  }

  const renderTable = () => {
    const totalMembersCount = stats?.totalMemberCount
    return (
      <div className={styles.container}>
        {(isLoading || members.length > 0) && (
          <div>
            <p className={styles.results}>
              {stats &&
                totalMembersCount <= 1000 &&
                `${t('edit-members-total-members')} ${totalMembersCount}`}
            </p>
            <TableContainer
              sx={{
                maxWidth: '867px',
                paddingRight: isMobileView ? '51px' : '0',
                maxHeight: '360px',
              }}
            >
              <Table stickyHeader className={styles.userTable}>
                <TableHead>{renderTableHead()}</TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell sx={{ border: 'none' }} colSpan={6}>
                        <div className={styles.loaderContainer}>
                          <div className={styles.loading}>{t('loading')}</div>
                          <CircularProgress
                            sx={{
                              color: 'black',
                            }}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    renderTableBody()
                  )}
                </TableBody>
              </Table>
              {members.length > 10 && (
                <TablePagination
                  sx={{ margin: 2 }}
                  rowsPerPageOptions={[10, 25, 100]}
                  component="div"
                  count={members.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </TableContainer>
          </div>
        )}
      </div>
    )
  }

  const renderEmptyUsersMessage = () => {
    if (!isLoading && members.length == 0) {
      return (
        <div style={{ marginTop: '100px' }}>{t('edit-members-no-user-found')}</div>
      )
    }
    return null
  }

  return (
    <>
      {renderTable()}
      {renderEmptyUsersMessage()}
    </>
  )
}

export default GroupMembers
